import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { SectionCard, formatNumber } from '../../../components/design-system/admin';
import type { Assumptions } from '../../../common/models/unit-economics.model';
import { money } from '../costs/cost-format';

const PROVIDER: Record<string, string> = { vast: 'Vast', runpod: 'RunPod', azure: 'Azure', openrouter: 'OpenRouter', fal: 'fal' };

function Rules({ items }: { items: ReactNode[] }) {
  return (
    <ul className="flex list-disc flex-col gap-1.5 ps-5 text-sm leading-relaxed text-muted-foreground marker:text-muted-foreground/60">
      {items.map((x, i) => (
        <li key={i}>{x}</li>
      ))}
    </ul>
  );
}

/**
 * Every rule and constant the page counts with, filled in from what the API
 * used (so a changed constant shows here at once): revenue, direct costs, the
 * shared bills and their drivers, the flags, value, the review, currencies.
 */
export function AssumptionsPanel({ assumptions }: { assumptions: Assumptions | null }) {
  const { t } = useTranslation();
  if (!assumptions) return <Skeleton className="h-96 rounded-xl" />;
  const a = assumptions;
  // An array of rules; a missing translation comes back as its key, shown as one line.
  const list = (key: string, values: Record<string, unknown> = {}): string[] => {
    const v = t(key, { returnObjects: true, ...values }) as unknown;
    return Array.isArray(v) ? v.map(String) : [String(v)];
  };
  const th = a.thresholds;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="ue-assumptions">
      <p className="text-sm text-muted-foreground lg:col-span-2">{t('UNIT_ECONOMICS.ASSUMPTIONS.DESCRIPTION')}</p>
      <SectionCard title={t('UNIT_ECONOMICS.ASSUMPTIONS.REVENUE')}>
        <Rules items={list('UNIT_ECONOMICS.ASSUMPTIONS.REVENUE_RULES')} />
      </SectionCard>
      <SectionCard title={t('UNIT_ECONOMICS.ASSUMPTIONS.COSTS')}>
        <Rules items={list('UNIT_ECONOMICS.ASSUMPTIONS.COST_RULES', { usdPerCredit: money(a.generation.usdPerCredit), margin: `${formatNumber(a.generation.margin, 1)}×` })} />
      </SectionCard>
      <SectionCard title={t('UNIT_ECONOMICS.ASSUMPTIONS.POOLS')}>
        <Rules
          items={[
            ...a.pools.map((p) => t(`UNIT_ECONOMICS.ASSUMPTIONS.POOL_RULES.${p.key}`, { providers: p.providers.map((x) => PROVIDER[x] || x).join(' + ') })),
            t('UNIT_ECONOMICS.ASSUMPTIONS.POOL_NOTE'),
          ]}
        />
      </SectionCard>
      <SectionCard title={t('UNIT_ECONOMICS.ASSUMPTIONS.FLAGS')}>
        <Rules
          items={[
            t('UNIT_ECONOMICS.ASSUMPTIONS.FLAG_RULES.loss_making'),
            t('UNIT_ECONOMICS.ASSUMPTIONS.FLAG_RULES.underpriced', { pct: th.underpricedDiscountPct, base: money(th.rateCardBaseMonthly) }),
            t('UNIT_ECONOMICS.ASSUMPTIONS.FLAG_RULES.renewal_soon', { days: th.renewalSoonDays }),
            t('UNIT_ECONOMICS.ASSUMPTIONS.FLAG_RULES.no_usage_30d', { days: th.noUsageDays }),
            t('UNIT_ECONOMICS.ASSUMPTIONS.FLAG_RULES.trial_cost_high', { usd: money(th.trialCostHighUsd) }),
            t('UNIT_ECONOMICS.ASSUMPTIONS.FLAG_RULES.trial_ending', { days: th.trialEndingDays }),
          ]}
        />
      </SectionCard>
      <SectionCard title={t('UNIT_ECONOMICS.ASSUMPTIONS.VALUE')}>
        <Rules items={list('UNIT_ECONOMICS.ASSUMPTIONS.VALUE_RULES')} />
      </SectionCard>
      <SectionCard title={t('UNIT_ECONOMICS.ASSUMPTIONS.REVIEW')}>
        <Rules
          items={list('UNIT_ECONOMICS.ASSUMPTIONS.REVIEW_RULES', {
            maxPaying: a.review.maxPaying,
            days: a.review.windowDays,
            newDays: a.review.newFeatureDays,
            trend: a.review.trendThresholdPct,
            lookback: a.review.lookbackDays,
          })}
        />
      </SectionCard>
      <SectionCard title={t('UNIT_ECONOMICS.ASSUMPTIONS.FX')} className="lg:col-span-2">
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t('UNIT_ECONOMICS.ASSUMPTIONS.FX_RULE', {
            rates: Object.entries(a.fx)
              .filter(([c]) => c !== 'usd')
              .map(([c, r]) => `1 ${c.toUpperCase()} = ${formatNumber(r, 3)} USD`)
              .join(' · '),
          })}
        </p>
      </SectionCard>
    </div>
  );
}
